import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { FileText, Download, Image, File, Archive, FileSpreadsheet, Eye } from 'lucide-react';
import { mockProjectFiles, mockUsers } from '../mock/data';

const ProjectFiles = ({ projectId }) => {
  const projectFiles = mockProjectFiles.filter(f => f.projectId === projectId);
  
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <Image className="w-5 h-5 text-blue-500" />;
    if (type === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
    if (type === 'application/zip' || type.includes('compressed')) return <Archive className="w-5 h-5 text-yellow-500" />;
    if (type.includes('spreadsheet') || type.includes('excel')) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const charterFiles = projectFiles.filter(f => f.category === 'charter');
  const taskAttachments = projectFiles.filter(f => f.category === 'task-attachment');

  return (
    <div className="space-y-6">
      {/* Project Charter Section */}
      {charterFiles.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
            <FileText className="w-5 h-5 text-purple-600" />
            <span>Project Charter</span>
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
              Official Document
            </Badge>
          </h3>
          <div className="space-y-3">
            {charterFiles.map((file) => {
              const uploader = mockUsers.find(u => u.id === file.uploadedBy);
              return (
                <Card key={file.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="p-3 bg-purple-100 rounded-lg">
                          {getFileIcon(file.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 truncate">{file.name}</h4>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="text-sm text-gray-500">{formatFileSize(file.size)}</span>
                            <span className="text-sm text-gray-500">•</span>
                            <div className="flex items-center space-x-2">
                              <Avatar className="w-5 h-5">
                                <AvatarImage src={uploader?.avatar} />
                                <AvatarFallback className="text-xs">{uploader?.name.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <span className="text-sm text-gray-500">{uploader?.name}</span>
                            </div>
                            <span className="text-sm text-gray-500">•</span>
                            <span className="text-sm text-gray-500">
                              {new Date(file.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Task Attachments Section */}
      {taskAttachments.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center space-x-2">
            <Archive className="w-5 h-5 text-gray-600" />
            <span>Task Attachments</span>
            <Badge variant="outline" className="bg-gray-50">
              {taskAttachments.length} files
            </Badge>
          </h3>
          <div className="grid grid-cols-1 gap-3">
            {taskAttachments.map((file) => {
              const uploader = mockUsers.find(u => u.id === file.uploadedBy);
              return (
                <Card key={file.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="p-3 bg-gray-100 rounded-lg">
                          {getFileIcon(file.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{file.name}</h4>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                            <span className="text-xs text-gray-500">•</span>
                            <div className="flex items-center space-x-2">
                              <Avatar className="w-4 h-4">
                                <AvatarImage src={uploader?.avatar} />
                                <AvatarFallback className="text-xs">{uploader?.name.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <span className="text-xs text-gray-500">{uploader?.name}</span>
                            </div>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs text-gray-500">
                              {new Date(file.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {projectFiles.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Files Yet</h3>
            <p className="text-gray-600 mb-4">
              Files uploaded in task comments will appear here
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProjectFiles;
